import {
  RouteRequest,
  RouteResponse,
  ChatRequest,
  ChatResponse,
  Place,
  TravelMode,
  PreferenceQuickPick,
} from '../types';

// Use production API URL or fallback to local proxy for development
const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

// Helper function for API calls
async function fetchApi<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    ...options,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `API Error: ${response.status}`);
  }

  return response.json();
}

// Route API
export async function getRoute(params: RouteRequest): Promise<RouteResponse> {
  const queryParams = new URLSearchParams();
  queryParams.append('start', params.start);
  queryParams.append('end', params.end);
  if (params.mode) queryParams.append('mode', params.mode);
  if (params.preferences) queryParams.append('preferences', params.preferences);
  if (params.radius) queryParams.append('radius', params.radius.toString());
  if (params.max_time) queryParams.append('max_time', params.max_time.toString());
  if (params.alternative) queryParams.append('alternative', '1');

  return fetchApi<RouteResponse>(`/route?${queryParams.toString()}`);
}

export async function recalculateRoute(params: {
  start: string;
  end: string;
  mode?: TravelMode;
  waypoints: { lat: number; lng: number }[];
}): Promise<RouteResponse> {
  return fetchApi<RouteResponse>('/route/recalculate', {
    method: 'POST',
    body: JSON.stringify({
      start: params.start,
      end: params.end,
      mode: params.mode || 'DRIVING',
      waypoints: params.waypoints,
    }),
  });
}

export async function getPlaceDetails(params: {
  place_id?: string;
  name?: string;
  location?: Place['location'];
}): Promise<{
  name: string;
  formatted_address?: string;
  rating?: number;
  user_ratings_total?: number;
  photos?: string[];
  reviews?: Array<{
    author_name: string;
    rating: number;
    text: string;
    relative_time_description: string;
  }>;
  url?: string;
  opening_hours?: {
    open_now?: boolean;
    weekday_text?: string[];
  };
}> {
  return fetchApi('/place-details', {
    method: 'POST',
    body: JSON.stringify(params),
  });
}

// Chat/Preferences extraction API - NOW USING LANGGRAPH AGENT
export async function sendChatMessage(request: ChatRequest): Promise<ChatResponse> {
  return fetchApi<ChatResponse>('/chat', {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

// Text-to-speech API
export async function textToSpeech(
  text: string,
  language: string = 'en-US',
  signal?: AbortSignal
): Promise<Blob> {
  const response = await fetch(`${API_BASE_URL}/text-to-speech`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ text, language }),
    signal, // Support abort signal for cancellation
  });

  if (!response.ok) {
    throw new Error('Failed to generate speech');
  }

  return response.blob();
}

// Translation API
export async function translateText(
  text: string,
  sourceLang: string,
  targetLang: string
): Promise<{ translated_text: string }> {
  return fetchApi<{ translated_text: string }>('/translate', {
    method: 'POST',
    body: JSON.stringify({
      text,
      source_lang: sourceLang,
      target_lang: targetLang,
    }),
  });
}

// Generate a unique session ID
export function generateSessionId(): string {
  return `session_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}

// Get or create a persistent session ID from localStorage
// This ensures the same session_id is used as long as the user is in the app
const SESSION_ID_KEY = 'routex_session_id';

export function getOrCreateSessionId(): string {
  // Try to get existing session_id from localStorage
  const storedSessionId = localStorage.getItem(SESSION_ID_KEY);

  if (storedSessionId) {
    return storedSessionId;
  }

  // Generate a new session_id if none exists
  const newSessionId = generateSessionId();
  localStorage.setItem(SESSION_ID_KEY, newSessionId);
  return newSessionId;
}

// Force a brand-new conversation thread for the agent and persist it.
export function resetSessionId(): string {
  const newSessionId = generateSessionId();
  localStorage.setItem(SESSION_ID_KEY, newSessionId);
  return newSessionId;
}

export interface RoutexClientConfig {
  google_maps_api_key: string;
  preference_quick_picks: PreferenceQuickPick[];
}

// Config API — Maps key and shared preference presets from backend
export async function getConfig(): Promise<RoutexClientConfig> {
  return fetchApi<RoutexClientConfig>('/config');
}

export interface GeocodeResult {
  formatted_address: string | null;
  lat: number;
  lng: number;
}

export async function geocodeAddress(address: string): Promise<GeocodeResult> {
  return fetchApi<GeocodeResult>(`/geocode?address=${encodeURIComponent(address)}`);
}

export async function reverseGeocode(lat: number, lng: number): Promise<GeocodeResult> {
  return fetchApi<GeocodeResult>(`/reverse-geocode?lat=${lat}&lng=${lng}`);
}
